import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled from 'styled-components'
import { rem } from 'polished'
import zIndex from 'styles/tools/z-index'
import { withRouter, RouteComponentProps } from 'react-router'
import {
  bookingPaymentUrlPattern,
  bookingUrlPattern,
  checkoutPaymentUrlPattern,
  reviewUrlPattern,
  experiencesURLPattern,
  tripPlannerUrlPattern,
  checkoutUrlPattern,
} from 'constants/url'
import compose from 'lib/compose'
import { quoteEmailModalOpen, showCustomerSignatureModal } from 'actions/UiActions'
import TextButton from 'components/Luxkit/Button/TextButton'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import MyCartMenu from 'components/App/Header/HeaderMain/NavigationHeader/MyCartMenu'
import Group from 'components/utils/Group'
import config from 'constants/config'
import MultiCartItemModeToggle from 'components/App/Header/HeaderMain/NavigationHeader/MultiCartItemModeToggle'
import { isActiveLuxuryPlusMember } from 'loyalty/selectors/featureToggle'
import clsx from 'clsx'

const Roots = styled.div`
  display: flex;
  justify-content: center;
  padding: ${rem(20)};
  color: ${props => props.theme.palette.neutral.eight};
  text-align: center;
  background-color: ${({ theme }) => theme.palette.messaging.critical.main};
  font-weight: ${props => props.theme.font.primary.weight.bold};
  position: sticky;
  top: 0;
  z-index: ${zIndex.spoofingWarning};

  &.luxury-plus-member {
    background-color: ${({ theme }) => theme.palette.product.loyalty};
  }
`

const ButtonsContainer = styled(Group)`
  position: absolute;
  top: ${rem(14)};
  right: ${rem(14)};

  > * {
    margin-right: ${rem(8)};
  }
`

const BackButtonDesktop = styled(TextButton)`
  display: inline-flex;
  position: absolute;
  top: ${rem(14)};
  left: ${rem(20)};
`

const ArrowIcon = styled(LineArrowRightIcon)`
  transform: rotate(180deg);
`
interface Props extends RouteComponentProps<any> {
  isSpoofed: boolean;
  fullName: string;
  utmMedium: string;
  quoteEmailModalOpen: Utils.ActionDispatcher<typeof quoteEmailModalOpen>;
  showCustomerSignatureModal: Utils.ActionDispatcher<typeof showCustomerSignatureModal>;
  isExperienceOnlyPurchase: boolean;
  isFlightOnlyPurchase: boolean;
  isCruisePurchase: boolean;
  isBundlePurchase: boolean;
  isMultiItemMode: boolean;
  isActiveLuxuryPlusMember: boolean;
}

class SpoofingWarning extends Component<React.PropsWithChildren<Props>> {
  componentDidMount() {
    /* forceUpdate has been used exclusively due
    to there being a mismatch between the Client
    and the Server Side rendering. The Client
    does not know about the Auth service - so the App
    hydrating doesn’t add the component styles as it
    doesn’t think they should be there. However running
    forceUpdate will force re-evaluation and ensure
    the styles get added to the DOM. */
    this.forceUpdate()
  }

  openModal = () => {
    this.props.quoteEmailModalOpen()
  }

  openCustomerSignaturenModal = () => {
    this.props.showCustomerSignatureModal()
  }

  render() {
    const { fullName, isSpoofed, location, utmMedium, isExperienceOnlyPurchase, isCruisePurchase, isBundlePurchase, isMultiItemMode, isActiveLuxuryPlusMember } = this.props
    const isPaymentPage = bookingPaymentUrlPattern.test(location.pathname) || checkoutPaymentUrlPattern.test(location.pathname)
    const isBookingPage = bookingUrlPattern.test(location.pathname) || checkoutPaymentUrlPattern.test(location.pathname)
    const isTripPlannerPage = tripPlannerUrlPattern.test(location.pathname)
    const isReviewPage = reviewUrlPattern.test(location.pathname)
    const isCheckoutPage = checkoutUrlPattern.test(location.pathname)
    const isExperiencePage = experiencesURLPattern.test(location.pathname)
    const showBackButton = isBookingPage || isReviewPage || isCheckoutPage
    const showQuoteButton = (isPaymentPage || isReviewPage || isTripPlannerPage) && !isExperiencePage && !isExperienceOnlyPurchase && !isCruisePurchase && !isBundlePurchase && !isMultiItemMode
    const onBack = () => {
      this.props.history.goBack()
    }

    const isStoreStaff = utmMedium === 'popup_store'
    if (!isSpoofed) {
      return null
    }

    return (
      <Roots className={clsx({ 'luxury-plus-member': isActiveLuxuryPlusMember })}>
        {showBackButton && <div>
          <BackButtonDesktop kind="tertiary" outdent="left" startIcon={<ArrowIcon size="XS" />} variant="ghost" onClick={onBack}>
            Back
          </BackButtonDesktop>
        </div> }
        <Group direction="horizontal" gap={16}><div>Acting as {fullName}. {isActiveLuxuryPlusMember && 'LuxuryPlus+ Member'}</div></Group>
        <ButtonsContainer direction="horizontal">
          {showQuoteButton && isStoreStaff && <TextButton kind="secondary" variant="dark" onClick={this.openCustomerSignaturenModal}>Capture Signature</TextButton>}
          {showQuoteButton && <TextButton kind="secondary" variant="dark" onClick={this.openModal}>Send Quote</TextButton>}
          {config.UNIVERSAL_CHECKOUT_SPOOFED_MULTI_CART_ITEMS_ENABLED && !isCheckoutPage && <MultiCartItemModeToggle />}
          {config.UNIVERSAL_CHECKOUT_SPOOFED_MULTI_CART_ITEMS_ENABLED && <MyCartMenu /> }
        </ButtonsContainer>
      </Roots>
    )
  }
}

const mapStateToProps = (state: App.State) => ({
  isSpoofed: state.auth.account.isSpoofed,
  fullName: state.auth.account.fullName,
  utmMedium: state.utm.medium,
  isExperienceOnlyPurchase: isExperienceOnlyPurchase(state),
  isFlightOnlyPurchase: isFlightOnlyPurchase(state),
  isCruisePurchase: isCruisePurchase(state),
  isBundlePurchase: isBundlePurchase(state),
  isMultiItemMode: state.checkout.cart.isMultiItemMode,
  isActiveLuxuryPlusMember: isActiveLuxuryPlusMember(state),
})

const isExperienceOnlyPurchase = (state: App.State) => {
  const items = state.checkout.cart.items
  return items.length ? items.every(item => item.itemType === 'experience') : false
}

const isCruisePurchase = (state: App.State) => {
  const items = state.checkout.cart.items
  return items.length ? items.some(item => item.itemType === 'cruise') : false
}

const isBundlePurchase = (state: App.State) => {
  const items = state.checkout.cart.items
  return items.length ? items.some(item => item.itemType === 'bundleAndSave') : false
}

const isFlightOnlyPurchase = (state: App.State) => {
  const items = state.checkout.cart.items
  return items.length ? items.every(item => item.itemType === 'flight') : false
}

const mapDispatchToProps = {
  quoteEmailModalOpen,
  showCustomerSignatureModal,
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default compose(
  withRouter,
  connector,
)(SpoofingWarning)
